import { useEffect, useMemo, useRef, useState } from 'react'
import { getEventPinConfig, getPlacePinConfig } from '../../lib/pinConfig.js'
import CategoryFilterBar from './CategoryFilterBar.jsx'
import DetailCard from '../DetailCard.jsx'
import { extractMapPosition, getDefaultMapOptions, loadGoogleMaps } from './mapUtils.js'
import { logError } from '../../lib/errorLogger.js'

export default function MapView({
    apiKey,
    results,
    selectedId,
    selectedItem,
    onSelect,
    onIdle,
    onReady,
    isLoading = false,
    className = '',
    categoryOptions = [],
    activeCategoryKeys = [],
    onToggleCategory,
    onClearCategories,
}) {
    const containerRef = useRef(null)
    const mapRef = useRef(null)
    const markersRef = useRef(new Map())
    const markerLibRef = useRef(null)
    const idleListenerRef = useRef(null)
    const [status, setStatus] = useState('idle')
    const [loadError, setLoadError] = useState('')

    const hasActiveFilters = Array.isArray(activeCategoryKeys) && activeCategoryKeys.length > 0

    const hasResults = useMemo(
        () => results.some(item => Number.isFinite(Number(item?.lat_y)) && Number.isFinite(Number(item?.lon_x))),
        [results],
    )

    useEffect(() => {
        let cancelled = false

        setStatus('loading')
        setLoadError('')

        loadGoogleMaps(apiKey)
            .then(async google => {
                if (cancelled) {
                    return
                }
                const { Map } = await google.maps.importLibrary('maps')
                const markerLib = await google.maps.importLibrary('marker')
                markerLibRef.current = markerLib

                const map = new Map(containerRef.current, getDefaultMapOptions())
                mapRef.current = map
                setStatus('ready')
                onReady?.(extractMapPosition(map))

                if (idleListenerRef.current) {
                    idleListenerRef.current.remove()
                }
                idleListenerRef.current = map.addListener('idle', () => {
                    onIdle?.(extractMapPosition(map))
                })
            })
            .catch(error => {
                if (cancelled) {
                    return
                }
                logError('MAP_LOAD_ERROR', 'Failed to load Google Maps', {
                    message: error.message,
                    stack: error.stack,
                    apiKey: apiKey ? 'present' : 'missing',
                })
                setLoadError(error.message)
                setStatus('error')
            })

        return () => {
            cancelled = true
            if (idleListenerRef.current) {
                idleListenerRef.current.remove()
                idleListenerRef.current = null
            }
        }
    }, [apiKey, onIdle, onReady])

    useEffect(() => {
        const markerLib = markerLibRef.current
        const map = mapRef.current
        if (!markerLib || !map) {
            return
        }

        // Clean up old markers
        try {
            markersRef.current.forEach(entry => {
                if (entry.marker) {
                    entry.marker.map = null
                }
            })
            markersRef.current.clear()
        } catch (error) {
            logError('MARKER_CLEANUP_ERROR', 'Failed to clean up markers', {
                message: error.message,
                stack: error.stack,
            })
        }

        const nextMarkers = new Map()

        // Limit markers on mobile to prevent memory issues
        const isMobile = window.innerWidth < 768
        const maxMarkers = isMobile ? 200 : 1000
        const itemsToRender = results.slice(0, maxMarkers)

        if (results.length > maxMarkers) {
            logError('MARKER_LIMIT', `Limiting markers to ${maxMarkers} (total: ${results.length})`, {
                isMobile,
                totalResults: results.length,
            })
        }

        itemsToRender.forEach(item => {
            try {
                const id = item.id
                const lat = Number(item?.lat_y)
                const lng = Number(item?.lon_x)
                if (!id || !Number.isFinite(lat) || !Number.isFinite(lng)) {
                    return
                }

                const pinConfig = item?.searchType === 'places'
                    ? getPlacePinConfig(item?.category ?? item?.ml_category)
                    : getEventPinConfig(item?.category ?? item?.ml_category)

                const pinElement = new markerLib.PinElement(pinConfig)
                pinElement.element.classList.add('map-pin')

                const marker = new markerLib.AdvancedMarkerElement({
                    map,
                    position: { lat, lng },
                    title: item?.name ?? item?.place_name ?? 'Location',
                    content: pinElement.element,
                })

                marker.addListener('click', () => {
                    onSelect?.(id)
                })

                nextMarkers.set(id, { marker, item })
            } catch (error) {
                logError('MARKER_CREATE_ERROR', 'Failed to create marker', {
                    message: error.message,
                    stack: error.stack,
                    itemId: item?.id,
                })
            }
        })

        markersRef.current = nextMarkers
    }, [results, onSelect])

    useEffect(() => {
        const map = mapRef.current
        if (!map) {
            return
        }

        markersRef.current.forEach((entry, id) => {
            const markerNode = entry.marker?.content
            if (!markerNode) {
                return
            }
            if (id === selectedId) {
                markerNode.classList.add('map-pin-active')
                entry.marker.zIndex = 1000
                if (entry.marker.position) {
                    map.panTo(entry.marker.position)
                }
            } else {
                markerNode.classList.remove('map-pin-active')
                entry.marker.zIndex = null
            }
        })
    }, [selectedId])

    return (
        <div className={`overflow-hidden rounded-2xl border border-purple-200 bg-white shadow-sm ${className}`}>
            <div className="relative h-[24rem] w-full">
                <div ref={containerRef} className="h-full w-full" />

                {status === 'loading' && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60 text-sm text-slate-600">
                        Loading map...
                    </div>
                )}

                {status === 'error' && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-red-600">
                        {loadError || 'We could not load the map. Set VITE_GOOGLE_MAPS_API_KEY and reload.'}
                    </div>
                )}

                {status === 'ready' && isLoading && (
                    <div className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-white/90 px-4 py-1 text-sm font-medium text-slate-600 shadow">
                        Searching this area...
                    </div>
                )}

                {status === 'ready' && !isLoading && !hasResults && (
                    <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-xl bg-white/90 p-4 text-center text-sm text-slate-600 shadow">
                        {hasActiveFilters
                            ? 'No results match the selected categories right now.'
                            : 'Pick a date and search to see events and places plotted on the map.'}
                    </div>
                )}

                {status === 'ready' && selectedItem && (
                    <DetailCard
                        item={selectedItem}
                        variant="overlay"
                        onClose={() => onSelect?.(null)}
                    />
                )}
            </div>

            <CategoryFilterBar
                categoryOptions={categoryOptions}
                activeCategoryKeys={activeCategoryKeys}
                hasActiveFilters={hasActiveFilters}
                onToggleCategory={onToggleCategory}
                onClearCategories={onClearCategories}
            />
        </div>
    )
}
